import { QueryStringParamService } from '../../../shared/http/query-string-param.service';
import { PayableInvoiceDto } from '../../../invoices/dto/payable-invoice.dto';
import { ApiCacheOptions } from 'src/app/shared';
import { InvoiceUpsertResultDto } from '../../../invoices/dto/invoice-upsert-result.dto';
import { InvoiceUpsertDto } from '../../../invoices/dto/invoice-upsert.dto';
import { PayOffInvoicesDto } from '../dto/pay-off-invoices.dto';
import { Injectable } from '@angular/core';
import { ApiService } from '../../../shared/api/api.service';
import { UtilService } from '../../../shared/util/util.service';
import { PagedResult } from '../../../shared/model/generic-paged-result.model';
import { IInvoiceService } from './iinvoice.service';
import { Invoice } from './invoice.model';
import { InvoiceLineItem } from './invoice-lineitem.model';
import { InvoiceSearchParams } from './invoice-search-params.model';
import { InvoiceSearchResult } from './invoice-search-result.model';
import { InvoiceAdvancedSearchResult } from './invoice-advanced-search-result.model';
import { InvoiceDto } from 'src/app/invoices/dto/invoice.dto';
import { InvoiceGenerationParamsDto } from './invoice-generation-params.dto';
import { InvoiceGenerationPreviewDto } from './invoice-generation-preview.dto';
import { InvoiceAdvancedSearchExportOptionsDto } from './advanced-search-export-options.dto';
import { InvoiceBulkSetDisableOnlinePaymentDto } from './dto/invoice-bulk-set-disable-online-payment.dto';
import { WriteOffInvoicesDto } from '../dto/write-off-invoices.dto';
import { FinancialApiService } from '../financial-api.service';
import { formatDate } from '@angular/common';
import { ModifiableInvoicesDto } from '../dto/modifiable-invoices.dto';
import { InvoiceRebalanceDto } from '../dto/invoice-rebalance.dto';
import { InvoiceAccountingPackagesDto } from './InvoiceAccountingPackages.dto';

@Injectable()
export class InvoiceService implements IInvoiceService {
  public _apiService: ApiService;
  public _utilService: UtilService;

  public constructor(
    apiService: ApiService,
    utilService: UtilService,
    private _queryStringParamService: QueryStringParamService,
    private _financialApiService: FinancialApiService
  ) {
    this._apiService = apiService;
    this._utilService = utilService;
  }
  public getOpenInvoices = (): Promise<Array<Invoice>> => {
    return this._apiService.get('Invoices?OpenInvoicesOnly=true').then((data: InvoiceSearchResult) => {
      return Promise.resolve(data.Result);
    });
  };

  public search = (searchParams: InvoiceSearchParams): Promise<PagedResult<InvoiceDto[]>> => {
    return this._apiService.getPaged('Invoices', searchParams);
  };

  public searchPayableInvoices(searchParams: InvoiceSearchParams): Promise<PagedResult<PayableInvoiceDto[]>> {
    return this._apiService.post('Invoices/SearchPayableInvoices', searchParams);
  }

  /**
   * perform an advanced search for invoices
   * @param searchParams
   */
  public advancedSearch(searchParams: any): Promise<PagedResult<Array<InvoiceAdvancedSearchResult>>> {
    return this._apiService.post('Invoice/AdvancedSearch', searchParams);
  }

  public getInvoiceAndInvoicePayments(id: number): Promise<InvoiceDto> {
    return this._apiService.get(`Invoice/${id}/GetInvoiceAndInvoicePayments`);
  }

  public getInvoiceUpsertDto(id: number, ignoreCache?: boolean): Promise<InvoiceUpsertDto> {
    if (ignoreCache) {
      let noCache = new ApiCacheOptions();
      noCache.CacheSeconds = 0;
      noCache.DeleteCache = true;
      noCache.DisableCaching = true;
      noCache.DisableSlidingCache = true;

      return this._financialApiService.get(`financial/v2/invoice/${id}/upsert-dto`, noCache);
    } else {
      return this._financialApiService.get(`financial/v2/invoice/${id}/upsert-dto`);
    }
  }

  public upsertInvoice(invoice: InvoiceUpsertDto): Promise<InvoiceUpsertResultDto> {
    invoice.InvoiceHeader.DateDue = new Date(formatDate(invoice.InvoiceHeader.DateDue, 'yyyy-MM-dd 12:00', 'en-US'));
    invoice.InvoiceHeader.InvoiceDate = new Date(formatDate(invoice.InvoiceHeader.InvoiceDate, 'yyyy-MM-dd 12:00', 'en-US'));
    for (let lineItem of invoice.LineItems) {
      lineItem.ApplyDate = new Date(formatDate(lineItem.ApplyDate, 'yyyy-MM-dd 12:00', 'en-US'));
      lineItem.ServicePeriod_StartPeriod = new Date(formatDate(lineItem.ServicePeriod_StartPeriod, 'yyyy-MM-dd 12:00', 'en-US'));
      lineItem.ServicePeriod_EndPeriod = new Date(formatDate(lineItem.ServicePeriod_EndPeriod, 'yyyy-MM-dd 12:00', 'en-US'));
    }

    if (invoice.PaymentHeader != null) {
      invoice.PaymentHeader.ApplyDate = new Date(formatDate(invoice.PaymentHeader.ApplyDate, 'yyyy-MM-dd 12:00', 'en-US'));
      invoice.PaymentHeader.PaymentDate = new Date(formatDate(invoice.PaymentHeader.PaymentDate, 'yyyy-MM-dd 12:00', 'en-US'));
    }

    return this._financialApiService.post(`financial/v2/invoice/${invoice.InvoiceHeader?.InvoiceNum ?? 0}/Upsert`, invoice);
  }

  public deleteInvoice(id: number): Promise<void> {
    return this._apiService.delete(`Invoices/${id}`);
  }

  public getTerms(): Promise<Array<string>> {
    return this._apiService.get('Invoice/Terms');
  }

  public ApplyRebalanceAdjustments(invoice: InvoiceRebalanceDto): Promise<InvoiceUpsertResultDto> {
    return this._financialApiService.post(`financial/v2/invoice/ApplyRebalanceAdjustments`, invoice);
  }
  /**
   * Get a preview of the invoices to generate based on the search params provided
   */
  public GetInvoiceGenerationPreview(searchParams: InvoiceGenerationParamsDto): Promise<PagedResult<Array<InvoiceGenerationPreviewDto>>> {
    return this._apiService.post(`Invoices/Generate/Preview`, searchParams);
  }

  /**
   * Get a preview of the invoices that will not be generated based on the search params provided
   */
  public GetInvoiceGenerationPreviewRejections(
    searchParams: InvoiceGenerationParamsDto
  ): Promise<PagedResult<Array<InvoiceGenerationPreviewDto>>> {
    return this._apiService.post(`Invoices/Generate/PreviewRejections`, searchParams);
  }

  /**
   * export the preview to csv based on the search params
   */
  public ExportInvoiceGenerationPreview(searchParams: InvoiceGenerationParamsDto): Promise<string> {
    return this._apiService.post(`Invoices/Generate/Preview/csv`, searchParams);
  }

  /**
   * export the rejections to csv based on the search params
   */
  public ExportInvoiceGenerationRejectionPreview(searchParams: InvoiceGenerationParamsDto): Promise<string> {
    return this._apiService.post(`Invoices/Generate/PreviewRejections/csv`, searchParams);
  }

  /**
   * generate the invoices based on the search params
   */
  public GenerateInvoices(invoiceGenerationParams: InvoiceGenerationParamsDto) {
    return this._apiService.post(`Invoices/Generate`, invoiceGenerationParams);
  }

  public payOffInvoices(dto: PayOffInvoicesDto): Promise<InvoiceUpsertResultDto> {
    return this._apiService.post('Invoice/PayOffInvoices', dto);
  }

  public areInvoicesModifiable(invoiceNumbers: Array<number>): Promise<ModifiableInvoicesDto> {
    let params = `?invoiceNums=${invoiceNumbers.join(',')}`; //this._queryStringParamService.deserializeObjectToParams({ invoiceNums: invoiceNumbers });
    return this._financialApiService.get(`financial/v2/invoice/AreInvoicesModifiable${params}`);
  }

  public exportSearch(options: InvoiceAdvancedSearchExportOptionsDto): Promise<string> {
    return this._apiService.post(`Invoice/AdvancedSearch/csv`, options);
  }

  public bulkSetDisableOnlinePayment(dto: InvoiceBulkSetDisableOnlinePaymentDto): Promise<void> {
    return this._apiService.put('Invoices/BulkSetDisableOnlinePayment', dto, true);
  }
  public getServicePeriods(): Promise<Array<string>> {
    return this._apiService.get('Invoice/ServicePeriods');
  }
  public getNextBatch(): Promise<string> {
    return this._apiService.get('Batch/GetNextBatch');
  }

  public getBatches(batchNum): Promise<number> {
    return this._apiService.get(`financial/v2/invoice/getBatches/${batchNum}`, this._apiService.getDefaultApiCacheOptionsForNoCache());
  }
  public deleteBatches(batchNum): Promise<void> {
    return this._apiService.delete(`financial/v2/invoice/DeleteBatches/${batchNum}`);
  }
  rebalanceInvoices(invoices: number[]) {
    return this._apiService.post('RevenueItems/OptionalRevenueItems/ProcessOptionalRevenueItems', { InvoiceNumbers: invoices });
  }

  public GetInvoiceAccountingPackages(invoiceNum: number): Promise<InvoiceAccountingPackagesDto> {
    return this._financialApiService.get(`financial/v2/accountingpackage/invoice/${invoiceNum}/accountingPackages`);
  }
}
